/**
 * Client workspace · Publishing tab.
 *
 * Renders two cards:
 *   1. Connected social accounts via `<SocialConnectionsCard>` —
 *      reads `list-social-connections` and, for client viewers,
 *      surfaces the live OAuth connect button (Task 113B). Wired to
 *      `start-social-connection-oauth` + top-level navigation to the
 *      provider's authorize URL. The duplicate placeholder panel
 *      that previously sat below this card (with disabled
 *      "ربط Instagram (قريباً)" / "ربط TikTok (قريباً)" buttons) was
 *      removed in 113B.
 *   2. Publish jobs for the active client (`list-publish-jobs?client_id=`)
 *      — Task 104C wired the per-client jobs filter.
 *
 * The per-content-item workflow surface lives on `ContentPage`; this
 * page is a read-only snapshot for the agency user to scan in-flight
 * publishing work without having to open each content item. The
 * "open content item" link below each row jumps to the content
 * workspace where the schedule / mark-published / mark-failed /
 * cancel buttons live.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §6.4`. The
 * publish-jobs list is wired (Task 104C); the OAuth connect flow is
 * live for Instagram (Task 113B), TikTok stays as scaffold (its
 * `complete-social-connection-oauth` callback returns 501 until
 * Content Posting API approval lands), Snapchat is manual-only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isApiError } from '../../api/client';
import {
  disconnectSocialConnection,
  listPublishJobs,
  listSocialConnections,
  startSocialConnectionOAuth,
} from '../../api/endpoints';
import { KMI_OAUTH_RETRY_REDIRECT_SS_KEY } from '../auth/SocialOAuthCallbackPage';
import type {
  ContentVariantPlatform,
  PublishJobRow,
  PublishJobStatus,
  SocialConnectionOAuthPlatform,
  SocialConnectionRow,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';
import { SocialConnectionsCard } from './SocialConnectionsCard';

type ConnectionsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly connections: ReadonlyArray<SocialConnectionRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type JobsState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly jobs: ReadonlyArray<PublishJobRow> }
  | { readonly status: 'error'; readonly error: unknown };

/**
 * Codex P1 fix (Task Meta-Business-Login review pass 1) — Arabic
 * copy table for start-OAuth errors. Replaces the previous
 * `err.body?.detail ?? err.message` fallback chain that surfaced
 * `KmiApiError`'s synthesized `"KMI API <code> (HTTP <status>)"`
 * English envelope verbatim into the Arabic publishing-page banner
 * whenever the wire body omitted `detail` (which is every 500 per
 * `_shared/errors.ts`).
 *
 * Markers added here MUST mirror the documented `_body` markers the
 * start-EF emits via `validation_error.fields._body`. Adding a new
 * marker is a one-line addition; the helper never falls back to the
 * raw envelope `KMI API <code> (HTTP <status>)` string.
 *
 * Mirrors the `describeOAuthError` pattern on
 * `SocialOAuthCallbackPage.tsx` (Task 113E precedent).
 */
const START_OAUTH_ERROR_COPY_AR: Record<string, string> = {
  // Future: marker-specific copy. Currently start-EF does not emit
  // structured `_body` markers — every failure path is a typed
  // KmiError that collapses to either `validation_error` (with no
  // `_body` field today) or `internal_error`. This table is the
  // hook for future markers.
};

const START_OAUTH_GENERIC_ERROR_AR =
  'تعذر بدء ربط الحساب. حاول مرة أخرى؛ إذا استمرّت المشكلة فاتصل بفريق الدعم.';

const START_OAUTH_INTERNAL_ERROR_AR =
  'تعذر بدء ربط الحساب بسبب خلل مؤقت في النظام. حاول مرة أخرى بعد قليل، ' +
  'وإذا استمرّت المشكلة فاتصل بفريق الدعم.';

/**
 * Pick the friendly Arabic copy for a start-OAuth error response.
 * Reads the `_body[0]` marker from the wire `fields` map (if any);
 * falls back to internal-error-specific copy for HTTP 500, and to
 * generic Arabic copy for everything else. NEVER returns the raw
 * `KmiApiError.message` envelope text.
 */
function describeStartOAuthError(err: unknown): string {
  if (!isApiError(err)) {
    return START_OAUTH_GENERIC_ERROR_AR;
  }
  const fields = err.body?.fields;
  if (fields && typeof fields === 'object') {
    const body = (fields as Record<string, ReadonlyArray<string>>)['_body'];
    const marker = Array.isArray(body) && typeof body[0] === 'string' ? body[0] : null;
    if (marker && START_OAUTH_ERROR_COPY_AR[marker]) {
      return START_OAUTH_ERROR_COPY_AR[marker];
    }
  }
  if (err.code === 'internal_error') {
    return START_OAUTH_INTERNAL_ERROR_AR;
  }
  return START_OAUTH_GENERIC_ERROR_AR;
}

export function PublishingPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const [connections, setConnections] = useState<ConnectionsState>({ status: 'loading' });
  const [jobs, setJobs] = useState<JobsState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);
  // Task 112C — OAuth start status. Surfaces inline above the
  // connections card so a failed init (env missing, RLS deny,
  // platform unsupported) shows up next to the click target.
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthInFlight, setOauthInFlight] = useState<SocialConnectionOAuthPlatform | null>(null);
  // Disconnect-flow status. The card owns the confirmation modal +
  // per-row error state; the page owns the transport (single source
  // of truth for the `disconnectSocialConnection` API call) and the
  // post-success refetch bump.
  const [disconnectInFlightId, setDisconnectInFlightId] = useState<string | null>(null);
  // Codex pass-1 P1-1 — synchronous in-flight ref. React's
  // `setDisconnectInFlightId` is async; two clicks in the same
  // event-loop tick can both observe the prior null. The ref check
  // synchronously gates duplicate handler invocations BEFORE any
  // state setter fires.
  const disconnectInFlightRef = useRef<string | null>(null);
  // Transient success banner shown above the card for a few seconds
  // after a successful disconnect — gives the user explicit feedback
  // beyond the row's status flip (which happens after the refetch).
  const [disconnectSuccessMsg, setDisconnectSuccessMsg] = useState<string | null>(null);

  const viewerRole: 'client' | 'agency' = useMemo(() => {
    // Derive from the most-recent active role's category. If the
    // user has both client + agency roles (rare), the FIRST role
    // returned by me-context is the active one. Falls back to
    // 'agency' for safety — agency view is read-only, client view
    // adds the connect buttons.
    const cat = me?.roles?.[0]?.category;
    return cat === 'client' ? 'client' : 'agency';
  }, [me]);

  const handleDisconnectClick = useCallback(
    async (input: {
      readonly connectionId: string;
      readonly platform: 'instagram' | 'tiktok' | 'snapchat';
      readonly reason: string | null;
    }) => {
      // Codex pass-1 P1-1 + P1-2 — synchronous re-entrancy gate.
      // The state-based `disconnectInFlightId` check would silently
      // return on duplicate fires (resolving the Promise as success),
      // which the card would then render as a false-success modal
      // dismissal for a destructive action that NEVER reached the EF.
      // The ref check fires synchronously AND throws on duplicate so
      // the card's catch surfaces an error state.
      if (disconnectInFlightRef.current !== null) {
        throw new Error('disconnect_already_in_flight');
      }
      disconnectInFlightRef.current = input.connectionId;
      setDisconnectInFlightId(input.connectionId);
      setDisconnectSuccessMsg(null);
      try {
        await disconnectSocialConnection({
          input: {
            connection_id: input.connectionId,
            reason: input.reason,
          },
        });
        // Bump reloadKey → list-social-connections refetches → the
        // row's wire status flips to 'revoked' / archived → the card
        // re-renders it as 'disconnected' without the disconnect button.
        setReloadKey((n) => n + 1);
        setDisconnectSuccessMsg('تم فصل الحساب بنجاح.');
        // Auto-clear the success banner after a short visibility window.
        // The user has the durable row-status flip as the persistent
        // signal; this banner is a transient acknowledgment.
        window.setTimeout(() => setDisconnectSuccessMsg(null), 5000);
      } catch (err: unknown) {
        // Re-throw so the card's per-row error state surfaces. We
        // intentionally do NOT surface error.message in the parent
        // banner — generic copy in the card is the user-facing
        // message; this throw just propagates failure to the awaiter.
        // (`disconnectSocialConnection` returns rejected Promise on
        // any KmiApiError; the card's try/catch handles UX state.)
        throw err;
      } finally {
        disconnectInFlightRef.current = null;
        setDisconnectInFlightId(null);
      }
    },
    [],
  );

  const handleConnectClick = useCallback(
    async (platform: 'instagram' | 'tiktok' | 'snapchat') => {
      if (!clientId) return;
      if (platform === 'snapchat') {
        // Defensive — the card disables the Snapchat button, but
        // also short-circuit here in case a future code-path lands
        // here for any reason.
        setOauthError('سناب شات يدعم النشر اليدوي فقط في النسخة الأولى.');
        return;
      }
      setOauthError(null);
      setOauthInFlight(platform);
      try {
        const redirectBackTo = window.location.pathname + window.location.search;
        const data = await startSocialConnectionOAuth({
          input: {
            client_id: clientId,
            platform,
            redirect_back_to: redirectBackTo,
          },
        });
        // Task 113E — stash the redirect-back path in sessionStorage
        // so the OAuth callback page's "إعادة المحاولة" button can
        // route the user back here after a failed connect (without
        // reusing the consumed code/state — a fresh click on
        // "ربط الحساب" mints a new state). The value is a same-
        // origin path; `consumeRetryRedirect` in the callback page
        // re-validates it via `isSafeRedirectPath` before navigating.
        try {
          window.sessionStorage.setItem(
            KMI_OAUTH_RETRY_REDIRECT_SS_KEY,
            JSON.stringify({ path: redirectBackTo, ts: Date.now() }),
          );
        } catch {
          /* sessionStorage unavailable in privacy-mode contexts —
             tolerable; the callback page falls back to /clients */
        }
        // Top-level navigate to the provider. The provider redirects
        // back to META_REDIRECT_URI / TIKTOK_REDIRECT_URI which the
        // FE owns and which calls complete-social-connection-oauth.
        // Top-level navigation (not popup) keeps the OAuth flow
        // simple — no postMessage / popup-blocker edge cases.
        window.location.href = data.authorize_url;
      } catch (err: unknown) {
        // Codex P1 fix (Task Meta-Business-Login review pass 1) —
        // the previous fallback chain `err.body?.detail ?? err.message`
        // surfaced `err.message` (`KmiApiError`'s synthesized
        // `"KMI API <code> (HTTP <status>)"` envelope string) verbatim
        // in the Arabic UI whenever the wire body omitted `detail` —
        // including every 500 (`detail` is only allowed on
        // `validation_error` / `entitlement_denied` / `conflict` per
        // `_shared/errors.ts`). The most common path that hit this was
        // a missing `META_BUSINESS_LOGIN_CONFIG_ID` env var producing a
        // 500 with no actionable copy.
        //
        // Post-fix: route through a marker-aware Arabic copy table
        // similar to `describeOAuthError` on the callback page.
        // NEVER surface `err.message`. Specific markers get specific
        // Arabic copy; everything else falls through to a generic
        // Arabic copy that points operators at support.
        setOauthError(describeStartOAuthError(err));
        setOauthInFlight(null);
      }
    },
    [clientId],
  );

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setConnections({ status: 'loading' });
    listSocialConnections(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setConnections({ status: 'ready', connections: data.connections });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setConnections({ status: 'not-found' });
          return;
        }
        setConnections({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setJobs({ status: 'loading' });
    listPublishJobs({ clientId, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setJobs({ status: 'ready', jobs: data.jobs });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // 404 (no visibility / no client) is acceptable here — surface
        // an empty state rather than a noisy error banner.
        if (isApiError(err) && err.code === 'not_found') {
          setJobs({ status: 'ready', jobs: [] });
          return;
        }
        setJobs({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  if (!clientId) return null;

  return (
    <div className="stack-md">
      {/* Task 113B — single social connections surface. The duplicate
          "الحسابات الموصولة" placeholder card that previously sat
          below this card (with its own disabled "ربط Instagram (قريباً)"
          / "ربط TikTok (قريباً)" buttons) was removed. Loading /
          not-found / error states render here as the card's peers so
          the operator never sees both a card AND a stale fallback
          panel at the same time. */}
      {oauthError ? (
        <div className="banner banner-error" role="alert">
          {oauthError}
        </div>
      ) : null}
      {disconnectSuccessMsg ? (
        <div className="banner banner-success" role="status">
          {disconnectSuccessMsg}
        </div>
      ) : null}
      {connections.status === 'loading' ? <LoadingState title="جاري تحميل الحسابات…" /> : null}
      {connections.status === 'not-found' ? (
        <NotFoundState
          title="لا يمكن الوصول إلى نشر هذا العميل"
          description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح حساباته."
        />
      ) : null}
      {connections.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الحسابات"
          error={connections.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {connections.status === 'ready' ? (
        <SocialConnectionsCard
          viewerRole={viewerRole}
          connections={connections.connections}
          onConnectClick={
            viewerRole === 'client' && !oauthInFlight ? handleConnectClick : undefined
          }
          onReconnectClick={
            viewerRole === 'client' && !oauthInFlight ? handleConnectClick : undefined
          }
          onDisconnectClick={viewerRole === 'client' ? handleDisconnectClick : undefined}
          disconnectInFlightId={disconnectInFlightId}
        />
      ) : null}

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          {/*
            Task 175 PR-A · Issue 4 — drop the English parenthetical
            translation from the h2 + remove the visible EF-identifier
            badge that used to live in the section meta slot. The rest
            of the page copy is already Arabic-first; only those two
            technical strings were leaking.
          */}
          <h2>مهام النشر</h2>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          المهام المجدولة + المنشورة + الفاشلة + الملغاة لكل المحتوى المرتبط بهذا العميل. للتنفيذ
          (وسم كمنشور / فاشل / إلغاء) افتح صفحة المحتوى المرتبط.
        </p>
        {jobs.status === 'loading' ? <LoadingState title="جاري تحميل مهام النشر…" /> : null}
        {jobs.status === 'error' ? (
          <ErrorState
            title="تعذر تحميل مهام النشر"
            error={jobs.error}
            onRetry={() => setReloadKey((n) => n + 1)}
          />
        ) : null}
        {jobs.status === 'ready' ? (
          jobs.jobs.length === 0 ? (
            <EmptyState
              title="لا توجد مهام نشر بعد"
              description="حالما تجدول النشر من صفحة المحتوى، تظهر المهام هنا."
            />
          ) : (
            <PublishJobsTable clientId={clientId} jobs={jobs.jobs} />
          )
        ) : null}
      </div>
    </div>
  );
}

interface PublishJobsTableProps {
  readonly clientId: string;
  readonly jobs: ReadonlyArray<PublishJobRow>;
}

function PublishJobsTable({ clientId, jobs }: PublishJobsTableProps) {
  const sorted = useMemo(() => {
    return [...jobs].sort((a, b) => {
      // Newest scheduled_for first; ties broken by created_at desc.
      const sa = Date.parse(a.scheduled_for);
      const sb = Date.parse(b.scheduled_for);
      if (sa !== sb) return sb - sa;
      return Date.parse(b.created_at) - Date.parse(a.created_at);
    });
  }, [jobs]);
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>المحتوى</th>
          <th>المنصة</th>
          <th>الحالة</th>
          <th>موعد النشر</th>
          <th>إجراءات</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((job) => {
          const ci = job.publish_targets?.content_variants?.content_items;
          const target = job.publish_targets?.target ?? null;
          return (
            <tr key={job.id}>
              <td>
                <span className="name">{ci?.title ?? '—'}</span>
              </td>
              <td>
                <span className="pill pill-info">
                  {target ? variantPlatformLabel(target) : '—'}
                </span>
              </td>
              <td>
                <span className={`pill pill-${publishJobStatusVariant(job.status)}`}>
                  {publishJobStatusLabel(job.status)}
                </span>
              </td>
              <td style={{ direction: 'ltr', textAlign: 'start' }}>
                {new Date(job.scheduled_for).toLocaleString('ar-SA')}
              </td>
              <td>
                {ci ? (
                  <Link to={`/app/clients/${clientId}/content?item=${ci.id}`} className="btn-text">
                    فتح المحتوى ↗
                  </Link>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function variantPlatformLabel(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'Instagram · منشور';
    case 'instagram_story':
      return 'Instagram · ستوري';
    case 'instagram_reel':
      return 'Instagram · ريل';
    case 'facebook_post':
      return 'Facebook · منشور';
    case 'facebook_story':
      return 'Facebook · ستوري';
    case 'tiktok_video':
      return 'TikTok · فيديو';
    case 'snapchat_story':
      return 'Snapchat · ستوري';
    default:
      return platform;
  }
}

function publishJobStatusLabel(status: PublishJobStatus): string {
  switch (status) {
    case 'scheduled':
      return 'مجدول';
    case 'processing':
      return 'قيد التنفيذ';
    case 'succeeded':
      return 'منشور';
    case 'failed':
      return 'فشل';
    case 'cancelled':
      return 'ملغى';
    default:
      return status;
  }
}

function publishJobStatusVariant(status: PublishJobStatus): string {
  switch (status) {
    case 'scheduled':
      return 'review';
    case 'processing':
      return 'info';
    case 'succeeded':
      return 'active';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'archived';
    default:
      return 'draft';
  }
}
